import {useCallback, useEffect, useRef, useState} from "react";
import type {Product} from "@zaaaz/shared";
import {ProductCard} from "./ProductCard";

type Props = {
  products: Product[];
  /** Visible cards per arrow click (defaults to visibleCount or 1). */
  scrollPages?: number;
  /** Lock viewport to N slides (e.g. 3 on PDP). Adds cc-productCarousel--3up. */
  visibleCount?: 3 | 4;
};

export function ProductCarousel({products, scrollPages, visibleCount}: Props) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const pageSize = scrollPages ?? visibleCount ?? 1;

  const updateArrows = useCallback(() => {
    const el = viewportRef.current;
    if(!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanPrev(el.scrollLeft > 2);
    setCanNext(el.scrollLeft < maxScroll - 2);
  }, []);

  useEffect(() => {
    updateArrows();
  }, [products, updateArrows]);

  useEffect(() => {
    const el = viewportRef.current;
    if(!el) return;
    el.addEventListener("scroll", updateArrows, {passive: true});
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      ro.disconnect();
    };
  }, [updateArrows]);

  const scroll = useCallback((dir: -1 | 1) => {
    const el = viewportRef.current;
    if(!el) return;
    const slide = el.querySelector<HTMLElement>(".cc-productCarousel__slide");
    const gap = parseFloat(getComputedStyle(el).gap || "20") || 20;
    const step = slide ? (slide.offsetWidth + gap) * pageSize : el.clientWidth * 0.9;
    el.scrollBy({left: dir * step, behavior: "smooth"});
  }, [pageSize]);

  if(!products.length) return null;

  const showNav = visibleCount ? products.length > visibleCount : products.length > 1;
  const rootClass = ["cc-productCarousel", visibleCount === 3 ? "cc-productCarousel--3up" : "", visibleCount === 4 ? "cc-productCarousel--4up" : ""].filter(Boolean).join(" ");

  return (
    <div className={rootClass}>
      <div className="cc-productCarousel__viewport" ref={viewportRef}>
        {products.map((p) => (
          <div key={p.id} className="cc-productCarousel__slide">
            <ProductCard product={p} />
          </div>
        ))}
      </div>
      {showNav ? (
        <div className="owl-nav" aria-hidden={!canPrev && !canNext}>
          <button
            type="button"
            className={`owl-prev${canPrev ? "" : " isDisabled"}`}
            disabled={!canPrev}
            aria-label="Previous products"
            onClick={() => scroll(-1)}
          >
            <i className="fa fa-angle-left" />
          </button>
          <button
            type="button"
            className={`owl-next${canNext ? "" : " isDisabled"}`}
            disabled={!canNext}
            aria-label="Next products"
            onClick={() => scroll(1)}
          >
            <i className="fa fa-angle-right" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
